'use client';

import { Flex, IconButton } from '@coolblue-development/freddy';
import addToWishlistTrigger from '@coolblue-development/ga4-events/triggers/ComparisonTool/addToWishlistTrigger';
import removeItemFromComparisonTrigger from '@coolblue-development/ga4-events/triggers/ComparisonTool/removeItemFromComparisonTrigger';
import { Cross } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { WishlistToggleButton } from '@coolblue-development/next-wishlist/client';
import { useTranslation } from '@i18n/useTranslation';
import { useEffect, useState } from 'react';

const Actions = ({
  productId,
  productName,
  index,
  total_indices
}: {
  productId: number;
  productName: string;
  index: number;
  total_indices: number;
}) => {
  const { t } = useTranslation('webshop.compare');
  const { trackEvent } = useAnalytics();

  const analyticsProps = {
    index,
    total_indices,
    item_id: productId
  };

  function removeProductFromComparison() {
    trackEvent(removeItemFromComparisonTrigger(analyticsProps));
  }

  const [removalPath, setRemovalPath] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const path = window.location.pathname;
    const pathSegments = path.split('/');
    const productIDs = pathSegments.filter(part => !isNaN(Number(part)));

    const updatedIds = productIDs.filter(id => id.toString() !== productId.toString());
    const newPath = pathSegments
      .map((part: string) => (productIDs.includes(part) ? updatedIds.shift() : part))
      .join('/')
      .replace(/\/$/, '');

    setRemovalPath(newPath);
  }, [productId]);

  function toggleWishlist({ nextState }: { nextState: boolean }) {
    if (!nextState) return;

    trackEvent(
      addToWishlistTrigger({
        item_id: productId
      })
    );
  }

  return (
    <Flex
      alignItems='center'
      justifyContent='flex-end'
      sx={theme => ({
        'button, a': {
          padding: theme.spacing[200],
          [theme.breakpoint.minWidth('lg')]: {
            padding: '0.6rem'
          }
        },
        'button:first-of-type': {
          border: 'none',
          boxShadow: 'none',
          height: '3.2rem',
          '& > div': {
            display: 'flex',
            height: '1.6rem'
          },
          [theme.breakpoint.minWidth('lg')]: {
            height: '2.8rem'
          }
        },
        '& [aria-pressed="false"] path': {
          fill: theme.color.icon.subtle
        }
      })}
    >
      <WishlistToggleButton productId={productId} variant='compact' onClick={toggleWishlist} />

      <IconButton
        component='a'
        href={removalPath}
        onClick={removeProductFromComparison}
        size='xsmall'
        iconColor='base'
        aria-label={t('removeFromComparison', { productName })}
      >
        <Cross size='medium' />
      </IconButton>
    </Flex>
  );
};

export default Actions;
